import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import { filter, take, timeout } from 'rxjs/operators';

import { PlotType, SpatialDataHttpService, SupersededError } from '@jax-data-science/sci-image-visualizer';

import { environment } from '../../environments/environment';
import { MainState } from '../main/main.state';
import { FileFolder, IFile, IFileInfo, ImageFile, RawFileInfo } from '../main/models/file-info';
import { FilesService } from './files.service';

/** What jit-service's `/spatial/detect` says about a file or folder. */
export interface SpatialDetection {
  spatial: boolean;
  /** `xenium-zip` (a `*_xe_outs.zip`) or `xenium-folder` (an unzipped `outs/`). */
  kind?: string;
  /** The dataset id the `/spatial/{id}/…` routes take. */
  id?: string;
  name?: string;
}

/** A bundle's tissue image as it was opened: the opens it belongs to, and the file. */
interface OpenedImage {
  /** {@link SpatialOmicsService}'s dataset open. */
  generation: number;
  /** `FilesService`'s image open. */
  imageGeneration: number;
  image: ImageFile;
}

const XENIUM_ZIP_SUFFIX = '_xe_outs.zip';
const XENIUM_EXPERIMENT_FILE = 'experiment.xenium';

/**
 * Opens spatial-omics datasets (10x Xenium bundles) in the visualizer's Spatial omics
 * plot mode. jit-service reads the bundle in place and speaks the library's spatial
 * wire format, so the library's own {@link SpatialDataHttpService} is the data port;
 * this service only decides what in the file tree is a dataset and drives the open.
 *
 * A bundle folder carries its morphology image beside the data, so opening one also
 * opens that image through the ordinary image path and then switches the view to
 * Spatial omics over it. A zipped bundle has no readable image (it is compressed inside
 * the zip), and for a dataset without one the visualizer opens the Spatial omics view
 * by itself.
 */
@Injectable({ providedIn: 'root' })
export class SpatialOmicsService {
  private readonly url = environment.slideCropServer;
  /** One-shot "open this node as a dataset" intent from the context menu, consumed by
   *  the next display of that node (the same hand-off "Load as Stack" uses). */
  private pending: IFileInfo | null = null;
  /** Whether a dataset is published, so leaving it clears the spatial plot modes. */
  private open = false;
  /** Bumped by every {@link openDataset} and {@link clear}, so an answer that arrives after another
   *  dataset (or none) was opened is recognised as belonging to an earlier open and dropped. */
  private generation = 0;
  private readonly plotTypeRequest = new Subject<PlotType>();

  constructor(private http: HttpClient,
              private spatialData: SpatialDataHttpService,
              private filesService: FilesService,
              private mainState: MainState,
              private messageService: MessageService) {
    this.spatialData.configure({ baseUrl: this.url });
  }

  /** A file a plain click opens as a dataset: a Xenium zip, or a bundle's `experiment.xenium`. */
  static isDatasetFile(file: IFileInfo | null | undefined): boolean {
    if (!(file instanceof IFile)) return false;
    const name = (file.name ?? '').toLowerCase();
    return name.endsWith(XENIUM_ZIP_SUFFIX) || name === XENIUM_EXPERIMENT_FILE;
  }

  /** Whether the context menu offers "Open in Spatial Omics plot mode" — any folder (it may
   *  hold a Xenium or pre-built bundle, which only the server can tell), a dataset file, or an
   *  AnnData file. An `.h5ad` is offered here rather than opened on a click: DIANNE's STQ
   *  feature files are `.h5ad` too, and are not spatial datasets. */
  static canOpen(file: IFileInfo | null | undefined): boolean {
    return file instanceof FileFolder || SpatialOmicsService.isDatasetFile(file)
      || (file instanceof IFile && (file.name ?? '').toLowerCase().endsWith('.h5ad'));
  }

  /** The location `/spatial/detect` should look at: an `experiment.xenium` stands for its folder. */
  static detectTarget(file: IFileInfo): RawFileInfo {
    const raw = file.rawData;
    if (file instanceof IFile && (file.name ?? '').toLowerCase() === XENIUM_EXPERIMENT_FILE) {
      const rel = raw.relPath.replace(/\/+$/, '');
      const folder = rel.includes('/') ? rel.substring(0, rel.lastIndexOf('/')) : '';
      const name = folder.includes('/') ? folder.substring(folder.lastIndexOf('/') + 1) : folder;
      return { project: raw.project, name, relPath: folder, type: 'Folder' };
    }
    return raw;
  }

  /** A plot type the host should switch the mounted visualizer to. */
  getPlotTypeRequest$(): Observable<PlotType> {
    return this.plotTypeRequest.asObservable();
  }

  /** Mark `file` to be opened as a dataset when it is next displayed. */
  requestOpen(file: IFileInfo): void {
    this.pending = file;
  }

  /** True once for the file {@link requestOpen} marked. */
  consumeRequest(file: IFileInfo): boolean {
    const hit = this.pending === file;
    if (hit) this.pending = null;
    return hit;
  }

  detect(file: IFileInfo): Promise<SpatialDetection> {
    const info = this.filesService.encode(SpatialOmicsService.detectTarget(file));
    return firstValueFrom(this.http.get<SpatialDetection>(`${this.url}spatial/detect`, { params: { info } }));
  }

  /**
   * Open `file` as a dataset. Resolves false (with a message) when it is not one.
   * The caller has already put the visualizer on screen.
   */
  async openDataset(file: IFileInfo): Promise<boolean> {
    const generation = ++this.generation;
    this.mainState.setImageLoadingMessage('Loading spatial omics dataset...');
    this.mainState.setImageLoading(true);
    let detection: SpatialDetection;
    try {
      detection = await this.detect(file);
    } catch (err) {
      this.fail(file, err);
      return false;
    }
    if (!detection.spatial || !detection.id) {
      this.mainState.setImageLoading(false);
      this.messageService.add({ severity: 'warn', summary: 'Not a spatial omics dataset',
        detail: `${file.name} is not a spatial omics dataset: a 10x Xenium bundle (a *_xe_outs.zip, or a folder `
          + 'with experiment.xenium), a pre-built bundle (a folder with manifest.json and coords.bin), '
          + 'or an AnnData .h5ad with obsm/spatial.' });
      this.clear();
      return false;
    }
    try {
      const dataset = await this.spatialData.selectDataset(detection.id);
      this.open = true;
      const imageId = dataset.imageRef?.imageId;
      if (imageId) {
        await this.openImage(file, imageId, generation);
      } else {
        // No tissue image: the visualizer switches to Spatial omics on its own. The dataset is what
        // is on screen (no image publication says so).
        this.filesService.setShownFile(file);
        this.mainState.setImageLoading(false);
      }
      return true;
    } catch (err) {
      if (err instanceof SupersededError) return false; // a newer open took over
      this.fail(file, err);
      return false;
    }
  }

  /** Leave spatial mode: drop the dataset so its plot modes leave the selector. */
  clear(): void {
    this.generation++;
    this.pending = null;
    if (!this.open) return;
    this.open = false;
    this.spatialData.clear();
  }

  /** Open the bundle's morphology image, then show the observations over it. */
  private async openImage(bundle: IFileInfo, relPath: string, generation: number): Promise<void> {
    const name = relPath.substring(relPath.lastIndexOf('/') + 1);
    const image = new ImageFile({ project: bundle.rawData.project, name, relPath, type: 'File' }, false);
    this.filesService.openImageFile(image);
    const opened = { generation, imageGeneration: this.filesService.getImageOpenGeneration(), image };
    // Switch once the image has rendered: a plot-type change mid-load would be redrawn
    // over by the image's own first plot.
    await firstValueFrom(this.mainState.isImageLoading$().pipe(filter((loading) => !loading), take(1)));
    if (!this.open || generation !== this.generation) return;
    this.plotTypeRequest.next(PlotType.SPATIAL_OMICS);
    void this.warnIfImageStillCaching(opened);
  }

  /**
   * Whether `opened` is still what is on screen: no dataset was opened or left since
   * ({@link generation}), no other image was opened since (`FilesService`'s own open generation,
   * which moves before the new image is published, unlike the rendering file), and the file the
   * viewer renders is this image - the same project and bucket as well as the same path.
   */
  private isCurrent(opened: OpenedImage): boolean {
    if (!this.open || opened.generation !== this.generation) return false;
    if (opened.imageGeneration !== this.filesService.getImageOpenGeneration()) return false;
    const rendering = this.filesService.getRenderingFileInfo()?.rawData;
    const image = opened.image.rawData;
    return rendering?.relPath === image.relPath
      && rendering?.project?.bucket === image.project?.bucket
      && rendering?.project?.name === image.project?.name;
  }

  /**
   * Say so when the tissue image cannot be drawn yet because the server is still caching it: a
   * large image (a plate's tens of GB) is copied from the bucket first, and until it is there the
   * Spatial omics view, which draws the image from its tiles, shows the observations alone - which,
   * said nowhere, reads as a dataset without an image.
   *
   * Decided by the server's answer for the image's tile descriptor (`/tiles/info`, 202 while
   * caching), which is what the view needs to draw it - not by the cache or load-error flags,
   * which neither say the image drew nor see the viewer's own render errors. Any other answer
   * says nothing: 200 is drawable, and a failure is reported where the render fails. Nothing is
   * said either when the answer comes after another dataset or image was opened
   * ({@link isCurrent}). Sent to the permanent `center-toast` outlet,
   * so it shows whichever tab is open.
   */
  private async warnIfImageStillCaching(opened: OpenedImage): Promise<void> {
    const image = opened.image;
    let status: number;
    try {
      const response = await firstValueFrom(
        this.http
          .get(`${this.url}tiles/info`, { params: { info: this.filesService.encode(image.rawData) }, observe: 'response' })
          .pipe(timeout(15000)),
      );
      status = response.status;
    } catch {
      return;
    }
    if (status !== 202 || !this.isCurrent(opened)) return; // drawable, failed, or superseded
    this.messageService.add({
      key: 'center-toast',
      severity: 'warn',
      summary: 'Tissue image not shown yet',
      detail: `${image.name} is still being cached on the server (a large image can take several minutes). `
        + 'The observations are shown without it; open the dataset again once it is cached.',
      life: 15000,
    });
  }

  private fail(file: IFileInfo, err: unknown): void {
    const e = err as { error?: { error?: string; message?: string }; message?: string };
    const detail = e?.error?.error ?? e?.error?.message ?? e?.message ?? String(err);
    this.mainState.setImageLoading(false);
    this.messageService.add({ severity: 'error', summary: 'Could not open spatial omics dataset',
      detail: `${file.name}: ${detail}` });
    this.clear();
  }
}
